import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {useSetCrumbs} from '../../components/crumbtrail'
import {ElapsedAbsoluteTime} from '../../components/formatters'
import {
  PageActions,
  PageBody,
  PageHeader,
  PageHeaderContent
} from '../../components/page-styles'
import {Button} from '../../components/library/buttons'
import {Dot} from '../../components/library/dot'
import {
  DropdownBody,
  DropdownItem
} from '../../components/library/dropdown/content'
import {InputGroup, TextInput} from '../../components/library/form'
import {
  List,
  ListColumns,
  NoResultsPermissions,
  useListOrderBy
} from '../../components/library/list'
import {Tag} from '../../components/library/tag/tag'
import {Switch} from '../../components/library/toggles/switch'
import {FormModal} from '../../components/modals'
import {
  AddEnvironmentMutationVariables,
  EnvironmentDetailFragment,
  UpdateEnvironmentMutationVariables,
  useAddEnvironmentMutation,
  useEnvironmentListLazyQuery,
  useUpdateEnvironmentMutation
} from '../../data/workbench-api/generated/environment'
import {
  EnvironmentsOrderBy,
  PermissionAction,
  PermissionResource
} from '../../data/workbench-api/generated/types'
import {useEnvironment} from '../../environment'
import {useStateChanged} from '../../hooks/use-state-changed'
import {OrganisationNode} from '../../organisation'
import {EditNameModal} from './edit-name-modal'

export const EnvironmentList = ({
  organisation
}: {
  organisation: OrganisationNode
}) => {
  const organisationId = organisation.id
  const [orderBy, setOrderBy] = useListOrderBy<EnvironmentsOrderBy>()

  const {
    environment: currentEnvironment,
    setEnvironmentId: setCurrentEnvironment
  } = useEnvironment()
  const currentEnvironmentId = currentEnvironment?.id

  const canView =
    organisation.hasPermission(
      PermissionResource.Environment,
      PermissionAction.View
    ) ?? false

  const canEdit =
    organisation.hasPermission(
      PermissionResource.Environment,
      PermissionAction.Edit
    ) ?? false

  const canAdmin =
    organisation?.hasPermission(
      PermissionResource.Environment,
      PermissionAction.Admin
    ) ?? false

  const [query, {data, loading, refetch = () => null}] =
    useEnvironmentListLazyQuery({
      variables: {
        organisationId: organisationId,
        orderBy
      }
    })

  const environments = data?.environments?.nodes

  const [updateEnvironment] = useUpdateEnvironmentMutation({
    refetchQueries: ['GetOrganisationAccess'],
    onCompleted: result => {
      // when archiving current selected environment, select another as current
      // when restoring any environment, and no current environment, use the restored environment
      const updated = result?.updateEnvironment?.environment
      if (updated && updated.id === currentEnvironmentId && !updated.active) {
        const validEnvironments =
          environments?.filter(s => s.id !== updated.id && s.active) ?? []
        setCurrentEnvironment(
          validEnvironments.length ? validEnvironments[0].id : undefined
        )
      } else if (updated && updated.active && !currentEnvironmentId) {
        setCurrentEnvironment(updated.id)
      }
    }
  })

  useEffect(() => {
    if (canView) {
      query()
    }
  }, [query, canView])

  const [addNew, setAddNew] = useState<boolean>(false)
  const [editEnvironment, setEditEnvironment] =
    useState<EnvironmentDetailFragment>()

  const environmentColumns: ListColumns<
    EnvironmentDetailFragment,
    EnvironmentsOrderBy
  > = [
    {
      id: 'name',
      header: 'Name',
      accessor: environment => (
        <>
          <Dot
            active={
              currentEnvironment
                ? currentEnvironment.id === environment.id
                : false
            }
          />
          {currentEnvironment?.hasPermission(
            PermissionResource.Environment,
            PermissionAction.Edit
          ) ? (
            <a
              onClick={() => {
                setEditEnvironment(environment)
              }}
            >
              {environment.name}
            </a>
          ) : (
            <strong>{environment.name}</strong>
          )}
        </>
      )
    },
    {
      id: 'id',
      header: 'ID',
      sortKey: EnvironmentsOrderBy.IdAsc,
      accessor: 'id'
    },
    {
      id: 'created',
      header: 'Created',
      accessor: r => <ElapsedAbsoluteTime date={r.created} />
    },
    {
      id: 'updated',
      header: 'Updated',
      accessor: r => <ElapsedAbsoluteTime date={r.updated} />
    },
    {
      id: 'active',
      header: 'Active',
      sortKey: EnvironmentsOrderBy.ActiveAsc,
      accessor: r =>
        r.active ? (
          <Tag appearance="green">Active</Tag>
        ) : (
          <Tag appearance="light">Archived</Tag>
        )
    }
  ]

  useSetCrumbs(['Manage environments'])

  return (
    <>
      <Helmet>
        <title>Manage Environments</title>
      </Helmet>
      <PageHeader>
        <PageHeaderContent>
          <h2>Manage Environments</h2>
        </PageHeaderContent>

        <PageActions>
          <Button onClick={() => setAddNew(true)} disabled={!canEdit}>
            Add environment
          </Button>
        </PageActions>
      </PageHeader>

      <PageBody>
        <List<EnvironmentDetailFragment, EnvironmentsOrderBy>
          columns={environmentColumns}
          rows={environments}
          loading={loading}
          renderOverflow={(environment, closeDropdown) => (
            <Overflow
              canAdmin={canAdmin}
              canEdit={canEdit}
              edit={env => setEditEnvironment(env)}
              environment={environment}
              closeDropdown={closeDropdown}
              setActive={(env, active) =>
                updateEnvironment({
                  variables: {...env, active}
                })
              }
            />
          )}
          setOrderBy={setOrderBy}
          orderBy={orderBy}
          renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
        />

        {editEnvironment &&
          (canAdmin ? (
            <EditModal
              canAdmin={canAdmin}
              environment={editEnvironment}
              handleClose={() => setEditEnvironment(undefined)}
              onSaved={() => {
                refetch()
              }}
            />
          ) : canEdit ? (
            <EditNameModal
              environment={editEnvironment}
              handleClose={() => setEditEnvironment(undefined)}
              onSaved={() => {
                refetch()
              }}
            />
          ) : null)}
        {addNew && canEdit ? (
          <AddModal
            canAdmin={canAdmin}
            organisationId={organisationId}
            handleClose={() => setAddNew(false)}
            onSaved={() => {
              refetch()
            }}
          />
        ) : null}
      </PageBody>
    </>
  )
}

interface OverflowProps {
  environment: EnvironmentDetailFragment
  closeDropdown: () => void
  setActive: (environment: EnvironmentDetailFragment, active: boolean) => void
  edit: (environment: EnvironmentDetailFragment) => void
  canAdmin: boolean
  canEdit: boolean
}
const Overflow = ({
  environment,
  closeDropdown,
  setActive,
  edit,
  canAdmin,
  canEdit
}: OverflowProps) => (
  <DropdownBody>
    <DropdownItem disabled={!canEdit}>
      <a
        onClick={() => {
          edit(environment)
          closeDropdown()
        }}
      >
        Edit
      </a>
    </DropdownItem>

    {
      <DropdownItem disabled={!canAdmin}>
        <a
          onClick={() => {
            setActive(environment, !environment.active)
            closeDropdown()
          }}
        >
          {environment.active ? 'Archive' : 'Restore'}
        </a>
      </DropdownItem>
    }
  </DropdownBody>
)

interface EditModalProps {
  environment: EnvironmentDetailFragment
  handleClose: () => void
  canAdmin: boolean
  onSaved: () => void
}

const EditModal = ({
  environment: environment,
  handleClose,
  canAdmin,
  onSaved
}: EditModalProps) => {
  const [localObj, setLocalObj, dirty] =
    useStateChanged<UpdateEnvironmentMutationVariables>(environment)
  const [doMutation, {loading, error}] = useUpdateEnvironmentMutation({
    onCompleted: handleClose,
    refetchQueries: ['GetOrganisation']
  })

  return (
    <FormModal
      title={'Edit ' + environment.name}
      handleClose={handleClose}
      onSubmit={async () => {
        await doMutation({
          variables: localObj
        })
        onSaved()
      }}
      error={error}
      isValid={dirty && !loading && !!localObj.name}
    >
      <EnvironmentForm<UpdateEnvironmentMutationVariables>
        canAdmin={canAdmin}
        localObj={localObj}
        setLocalObj={setLocalObj}
      />
    </FormModal>
  )
}

interface AddModalProps {
  organisationId: string
  handleClose: () => void
  canAdmin: boolean
  onSaved: () => void
}
const AddModal = ({
  organisationId,
  handleClose,
  canAdmin,
  onSaved
}: AddModalProps) => {
  const [localObj, setLocalObj, dirty] =
    useStateChanged<AddEnvironmentMutationVariables>({
      organisationId,
      active: true,
      name: ''
    })
  const [doMutation, {loading, error}] = useAddEnvironmentMutation({
    onCompleted: handleClose,
    refetchQueries: ['GetOrganisationAccess']
  })

  return (
    <FormModal
      title="New environment"
      handleClose={handleClose}
      onSubmit={async () => {
        await doMutation({
          variables: localObj
        })
        onSaved()
      }}
      error={error}
      isValid={dirty && !loading && !!localObj.name}
    >
      <EnvironmentForm<AddEnvironmentMutationVariables>
        canAdmin={canAdmin}
        localObj={localObj}
        setLocalObj={setLocalObj}
      />
    </FormModal>
  )
}

interface EnvironmentFormProps<T> {
  localObj: T
  setLocalObj: React.Dispatch<React.SetStateAction<T>>
  canAdmin: boolean
}
const EnvironmentForm = <
  T extends UpdateEnvironmentMutationVariables | AddEnvironmentMutationVariables
>({
  localObj,
  setLocalObj,
  canAdmin
}: EnvironmentFormProps<T>) => (
  <InputGroup>
    <TextInput
      label="Give it a name..."
      upLabel="Environment name"
      value={localObj.name ?? ''}
      onChange={name => setLocalObj(c => ({...c, name}))}
    />
    {canAdmin && (
      <Switch
        label="Active"
        isForm={true}
        labelPosition="left"
        checked={!!localObj.active}
        onChange={active => setLocalObj(c => ({...c, active}))}
      />
    )}
  </InputGroup>
)
